import React, { useEffect, useReducer } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import SelectBox from "../src/components/SelectBox";
import { If } from "../src/utils/FormFunctions";
import toastr from "../../app/assets/javascripts/lib/toastr";

const reducer = (state, action) => {
  let nextId;
  let newOptionAttribute;
  switch (action.type) {
    case "setNonProfit":
      return { ...state, isNonProfitPrice: action.payload };
    case "togglePremiumYear":
      return { ...state, isPremiumYear: !state.isPremiumYear };
    case "setApplicantNumber":
      return { ...state, applicantNumber: action.payload };
    case "setTotalOptionPrice":
      return { ...state, totalOptionPrice: action.payload };
    case "setPricePerApplicant":
      return { ...state, pricePerApplicant: action.payload };
    case "setShowCaluculatedPrice":
      return { ...state, showCaluculatedPrice: action.payload };
    case "setApplicantAttributes":
      return { ...state, applicantAttributes: action.payload };
    case "setOptionAttributes":
      return { ...state, optionAttributes: action.payload };
    case "setDates":
      return { ...state, dates: action.payload };
    case "setDateRange":
      return { ...state, dateRange: action.payload };
    case "addOptionAttribute":
      nextId =
        state.optionAttributes.length > 0
          ? state.optionAttributes[state.optionAttributes.length - 1].id + 1
          : 1;
      newOptionAttribute = { id: nextId, selectedDate: "", applicants: 40 };
      return {
        ...state,
        optionAttributes: [...state.optionAttributes, newOptionAttribute],
      };
    case "removeOptionAttribute":
      return {
        ...state,
        optionAttributes: state.optionAttributes.filter(
          (optionAttribute, index) => index !== action.payload
        ),
      };
    case "updateOptionAttributes":
      return { ...state, optionAttributes: action.payload };
    default:
      return state;
  }
};

function PlanSimulator({ image_urls, is_logged_in_user, is_non_profit_user, prices }) {
  const { non_profit, profit } = prices;

  const [state, dispatch] = useReducer(reducer, {
    isNonProfitPrice: is_non_profit_user,
    isPremiumYear: true,
    applicantNumber: 40,
    totalOptionPrice: 0,
    pricePerApplicant: 0,
    showCaluculatedPrice: false,
    applicantAttributes: [],
    optionAttributes: [{ id: 1, selectedDate: 0 }],
  });

  useEffect(() => {
    buildDateRange();
    const newApplicantAttributes = buildApplicantAttributes();
    dispatch({
      type: "setApplicantAttributes",
      payload: newApplicantAttributes,
    });
  }, []);

  useEffect(() => {
    dispatch({ type: "setShowCaluculatedPrice", payload: false });
  }, [state.isNonProfitPrice, state.isPremiumYear, state.applicantNumber, state.optionAttributes]);

  const handleChange = (type, payload) => {
    dispatch({ type, payload });
  };

  const handleChangeNonProfitValue = (value) => handleChange("setNonProfit", value);
  const handleChangePremiumYearUserValue = () => handleChange("togglePremiumYear");

  const handleChangeOptionAttribute = (index, key, value) => {
    const newOptionAttributes = state.optionAttributes.map((item, i) =>
      i === index ? { ...item, [key]: value } : item
    );
    handleChange("updateOptionAttributes", newOptionAttributes);
  };

  const handleChangeSelectBoxValue = (index, value) => {
    const selectedDate = Object.values(value)[0];
    handleChangeOptionAttribute(index, "selectedDate", selectedDate);
  };

  const handleChangeYearlyApplicantValue = (index, e) => {
    const newValue = parseInt(e.target.value, 10);
    handleChangeOptionAttribute(index, "applicants", newValue);
  };

  const handleChangeApplicantNumber = (event) => {
    const newApplicantNumber = parseInt(event.target.value, 10);
    handleChange("setApplicantNumber", newApplicantNumber);
  };

  const handleUpdate = (type, payload) => {
    dispatch({ type, payload });
  };

  const handleAddOptionAttribute = () => handleChange("addOptionAttribute");
  const handleRemoveOptionAttribute = (index) => handleChange("removeOptionAttribute", index);

  const calculateTotalOptionPrice = (isPremiumYear) => {
    let optionPrice = 0;
    const { applicantNumber, isNonProfitPrice } = state;
    if (applicantNumber > 30) {
      optionPrice =
        Math.max(Math.ceil((applicantNumber - 30) / 10), 0) *
        (isNonProfitPrice
          ? non_profit.per_once_add_option_price
          : profit.per_once_add_option_price);
      if (isPremiumYear) {
        optionPrice *= 12;
      }
    }
    return optionPrice;
  };

  const validateBeforeCalculation = () => {
    if (state.applicantNumber === null || state.applicantNumber < 0) {
      toastr.error("応募人数は0以上の数字で入力してください");
      return false;
    } else if (isNaN(state.applicantNumber)) {
      handleChange("setApplicantNumber", 0);
    } else if (state.isNonProfitPrice === null) {
      toastr.error("法人格を選択してください");
      return false;
    } else {
      return true;
    }
  };

  const calculatePrice = () => {
    if (!validateBeforeCalculation()) {
      return;
    }
    const newTotalOptionPrice = calculateTotalOptionPrice(state.isPremiumYear);
    if (state.isPremiumYear) {
      const reBuildedApplicantAttributes = buildApplicantAttributes();
      const updatedApplicantAttributes = reBuildedApplicantAttributes.map((attribute) => {
        const foundOption = state.optionAttributes.find(
          (option) => option.selectedDate === attribute.month
        );
        if (!foundOption) {
          return attribute;
        } else if (isNaN(foundOption.applicants)) {
          return { ...attribute, applicants: state.applicantNumber };
        } else if (foundOption.applicants < 0) {
          toastr.error("月ごとの応募上限は0以上の数字で入力してください");
          return;
        } else {
          return { ...attribute, applicants: foundOption.applicants };
        }
      });
      handleUpdate("setApplicantAttributes", updatedApplicantAttributes);

      const totalOptionPrice = updatedApplicantAttributes.reduce((total, attribute) => {
        if (attribute.applicants > 30) {
          const additionalApplicants = Math.max(Math.ceil((attribute.applicants - 30) / 10), 0);
          const optionPrice =
            additionalApplicants *
            (state.isNonProfitPrice
              ? non_profit.per_once_add_option_price
              : profit.per_once_add_option_price);
          return total + optionPrice;
        }
        return total;
      }, 0);
      handleUpdate("setTotalOptionPrice", totalOptionPrice);

      const totalApplicants = updatedApplicantAttributes.reduce(
        (total, attribute) => total + attribute.applicants,
        0
      );
      calcAndUpdatePricePerApplicant(premiumYearlyPrice + totalOptionPrice, totalApplicants);
    } else {
      calcAndUpdatePricePerApplicant(
        premiumMonthlyPrice + newTotalOptionPrice,
        state.applicantNumber
      );
      handleUpdate("setTotalOptionPrice", newTotalOptionPrice);
    }
    handleUpdate("setShowCaluculatedPrice", true);
    scrollToBottom();
  };

  const scrollToBottom = () => {
    setTimeout(() => {
      window.scrollTo({
        top: document.body.scrollHeight,
        behavior: "smooth",
      });
    }, 100);
  };

  const calcAndUpdatePricePerApplicant = (totalPrice, applicants) => {
    if (applicants === 0 || isNaN(applicants)) {
      dispatch({ type: "setPricePerApplicant", payload: 0 });
      return;
    }
    const pricePerApplicant = Math.floor(totalPrice / applicants);
    dispatch({ type: "setPricePerApplicant", payload: pricePerApplicant });
  };

  const premiumMonthlyPrice = state.isNonProfitPrice
    ? non_profit.monthly_price
    : profit.monthly_price;
  const premiumYearlyPrice = state.isNonProfitPrice
    ? non_profit.per_year_price
    : profit.per_year_price;
  const total = state.isPremiumYear
    ? premiumYearlyPrice + state.totalOptionPrice
    : premiumMonthlyPrice + state.totalOptionPrice;

  const buildApplicantAttributes = () => {
    const newApplicantAttributes = Array.from({ length: 12 }, (_, index) => ({
      id: index + 1,
      month: index + 1,
      applicants: state.applicantNumber,
    }));

    return newApplicantAttributes;
  };

  const formatMonthAndDate = (date) => {
    const month = date.getMonth() + 1;
    const day = date.getDate();
    return `${month}/${day}`;
  };

  const buildDateRange = () => {
    const dateRanges = [];
    const today = new Date();
    const date = new Date();

    for (let i = 0; i < 12; i += 1) {
      const startDate = new Date(date);
      startDate.setMonth(startDate.getMonth() + i);
      const endDate = new Date(startDate);

      if (!hasValidDateInNextMonth(today)) {
        endDate.setDate(1);
        endDate.setMonth(endDate.getMonth() + 1);
        endDate.setMonth(endDate.getMonth() + 1);
        endDate.setDate(0);
      } else {
        endDate.setMonth(endDate.getMonth() + 1);
      }
      const formattedDate = `${formatMonthAndDate(startDate)}~${formatMonthAndDate(endDate)}`;
      dateRanges.push({ label: formattedDate, value: i + 1 });
      date.setDate(endDate.getDate());
    }
    handleUpdate("setDateRange", dateRanges);
  };

  const hasValidDateInNextMonth = (date) => {
    const nextMonth = new Date(date);
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    return nextMonth.getDate() === date.getDate();
  };

  return (
    <div className="planSimulator">
      <div className="planSimulationContainer">
        <div className="exampleContainer">
          <div className="exampleItem">
            <div className="orgContainer">
              <span className="orgTitle">Aさんの場合の例</span>
              <img className="orgImage" src={image_urls.man} />
              <p className="orgComment m_leftBalloon">
                <strong>12月</strong>にイベントを予定しているので、この月の応募上限を
                <strong>100名</strong>にしたい！
                <br />
                <br />
                でも、それ以外の月は30名までで十分かも。
              </p>
            </div>
            <div className="priceWrapper">
              <div className="priceContainer">
                <span className="priceTitle">発生する追加料金</span>
                <div className="priceItem">
                  <span className="isMonth">11月</span>
                  <span className="isApplicant">
                    <strong>24</strong>応募/月
                  </span>
                  <p className="isDesc">上限追加なし</p>
                  <span className="isPrice">0円</span>
                </div>
                <div className="priceItem">
                  <span className="isMonth">12月</span>
                  <span className="isApplicant">
                    <strong>87</strong>応募/月
                  </span>
                  <p className="isDesc isDescRed">上限を100応募に設定</p>
                  <span className="isPrice">
                    {(is_logged_in_user && !is_non_profit_user)
                      ? "26,400"
                      : "13,200"
                    }
                    円
                  </span>
                </div>
                <div className="priceItem">
                  <span className="isMonth">1月</span>
                  <span className="isApplicant">
                    <strong>30</strong>応募/月
                  </span>
                  <p className="isDesc">上限追加なし</p>
                  <span className="isPrice">0円</span>
                </div>
              </div>
              <p>
                ※応募上限数の枠は次月に繰り越しできません
                {!is_non_profit_user && is_logged_in_user && (
                  <React.Fragment>
                    <br />
                    ※その他の法人向けの価格を表示しております
                  </React.Fragment>
                )}
              </p>
            </div>
          </div>
          <div className="exampleItem">
            <div className="orgContainer">
              <span className="orgTitle">Bさんの場合の例</span>
              <img className="orgImage" src={image_urls.woman} />
              <p className="orgComment m_leftBalloon">
                夏休みの学生に参加してもらいたいので、<strong>7月</strong>
                にいつもより多く上限を設けたい！
                <br />
                <br />
                いつもは一律上限40名にしているけど、7月だけ<strong>無制限</strong>
                に設定しよう！
              </p>
            </div>
            <div className="priceWrapper">
              <div className="priceContainer">
                <span className="priceTitle">発生する追加料金</span>
                <div className="priceItem">
                  <span className="isMonth">6月</span>
                  <span className="isApplicant">
                    <strong>31</strong>応募/月
                  </span>
                  <p className="isDesc">上限を40応募に設定</p>
                  <span className="isPrice">
                    {(is_logged_in_user && !is_non_profit_user)
                      ? "4,400"
                      : "2,200"
                    }
                    円
                  </span>
                </div>
                <div className="priceItem">
                  <span className="isMonth">7月</span>
                  <span className="isApplicant">
                    <strong>57</strong>応募/月
                  </span>
                  <p className="isDesc isDescRed">上限を無制限に設定</p>
                  <span className="isPrice">
                    {(is_logged_in_user && !is_non_profit_user)
                      ? "13,200"
                      : "6,600"
                    }
                    円
                  </span>
                </div>
                <div className="priceItem">
                  <span className="isMonth">8月</span>
                  <span className="isApplicant">
                    <strong>29</strong>応募/月
                  </span>
                  <p className="isDesc">上限を40応募に設定</p>
                  <span className="isPrice">
                    0円
                  </span>
                </div>
              </div>
              <p>
                ※応募上限数の枠は次月に繰り越しできません
                {!is_non_profit_user && is_logged_in_user && (
                  <React.Fragment>
                    <br />
                    ※その他の法人向けの価格を表示しております
                  </React.Fragment>
                )}
              </p>
            </div>
          </div>
        </div>
        <h3>法人格</h3>
        <div className="checkBoxes">
          <input
            type="radio"
            id="non_profit_radio"
            name="is_non_profit_user"
            defaultChecked={is_non_profit_user && is_logged_in_user}
            onChange={() => handleChangeNonProfitValue(true)}
          />
          <label htmlFor="non_profit_radio">
            非営利法人
            {is_non_profit_user && is_logged_in_user && "（お客様はこちらが適用されます）"}
          </label>

          <input
            type="radio"
            id="profit_radio"
            name="is_non_profit_user"
            defaultChecked={!is_non_profit_user && is_logged_in_user}
            onChange={() => handleChangeNonProfitValue(false)}
          />
          <label htmlFor="profit_radio">
            その他の法人
            {!is_non_profit_user && is_logged_in_user && "（お客様はこちらが適用されます）"}
          </label>
        </div>
        <p className="statusDesc">
          ※非営利法人に当てはまるのは「NPO法人」「一般社団法人」「一般財団法人」「任意団体」などです。
          <br />
          　詳しくは<a href="https://activo.zendesk.com/hc/ja/articles/25446746032909" target="blank">こちらのページ</a>をご覧ください。
        </p>
        <h3>ご契約プラン</h3>
        <div className="checkBoxes">
          <label htmlFor="year_radio">
            <input
              type="radio"
              id="year_radio"
              name="is_premium_year_user"
              defaultChecked={state.isPremiumYear}
              onChange={handleChangePremiumYearUserValue}
            />
            年契約プレミアムプラン
          </label>

          <label htmlFor="month_radio">
            <input
              type="radio"
              id="month_radio"
              name="is_premium_year_user"
              onChange={handleChangePremiumYearUserValue}
              defaultChecked={!state.isPremiumYear}
            />
            月契約プレミアムプラン
          </label>
        </div>

        <h3>毎月の集めたい応募人数</h3>
        <div className="applicantsContainer">
          <input
            type="number"
            defaultValue={state.applicantNumber}
            className="u_disBlock"
            onChange={handleChangeApplicantNumber}
            step="10"
            min={0}
            required
          />
          <span>応募/月</span>
          <span className="isDesc">
            追加人数：{state.applicantNumber <= 30 ? 0 : state.applicantNumber - 30}応募(
            {calculateTotalOptionPrice(false).toLocaleString()}
            円)
          </span>
        </div>
        {state.isPremiumYear && (
          <div>
            <h3>月ごとに集めたい応募人数を変えたい方はこちらから追加</h3>
            <div className="addOptionContainer">
              {state.optionAttributes.map((item, index) => (
                <div key={item.id} className="addOptionList">
                  <SelectBox
                    id={`dates_${item.id}`}
                    name={`dates_${item.id}`}
                    items={state.dateRange}
                    value={item.selectedDate}
                    onChangeValue={(value) => handleChangeSelectBoxValue(index, value)}
                  />
                  は
                  <input
                    type="number"
                    defaultValue={item.applicants}
                    className="u_disBlock"
                    placeholder="例：40"
                    onChange={(e) => handleChangeYearlyApplicantValue(index, e)}
                    step="10"
                    min={0}
                  />
                  応募/月
                  <If condition={item.selectedDate}>
                    <button
                      className="isDeleteBtn"
                      type="button"
                      onClick={() => handleRemoveOptionAttribute(index)}
                    />
                  </If>
                </div>
              ))}
              <div>
                <button className="isAddBtn" type="button" onClick={handleAddOptionAttribute}>
                  条件を追加する
                </button>
              </div>
            </div>
          </div>
        )}
        <div className="calcBtn">
          <button type="button" onClick={calculatePrice}>
            料金を計算する
          </button>
        </div>
      </div>

      {state.showCaluculatedPrice && (
        <div className="calculatedContainer">
          <div className="titleContainer">
            <span>
              <h3 className="isTitle">最大の合計金額</h3>
              <p className="isSub">{state.isPremiumYear ? "※1年間での合計金額" : ""}</p>
            </span>
            <ul className="resultsContainer">
              <li>追加料金合計：{state.totalOptionPrice.toLocaleString()}円（税込）</li>
              {state.isPremiumYear ? (
                <li>年契約プレミアムプラン：{premiumYearlyPrice.toLocaleString()}円（税込）</li>
              ) : (
                <li>月契約プレミアムプラン：{premiumMonthlyPrice.toLocaleString()}円（税込）</li>
              )}
              <li className="isMain">合計：{total.toLocaleString()}円（税込）</li>
              <li>
                <strong>単価：{state.pricePerApplicant.toLocaleString()}円/応募</strong>
              </li>
              <li>※事前に応募上限数を設定することも可能です</li>
            </ul>
          </div>
          {state.isPremiumYear && (
            <dl className="yearlyTableContainer">
              <div className="overFlowXScroll">
                <div>
                  <dt className="isTitle" />
                  <dd className="isData">応募数</dd>
                  <dd className="isData">
                    応募上限
                    <br />
                    追加料金
                  </dd>
                </div>
                <div className="yearlyTable">
                  {state.applicantAttributes.map((item, index) => (
                    <div key={item.id}>
                      <dt className="isTitle">
                        {item.month}ヶ月目
                        <br />({state.dateRange[index].label})
                      </dt>
                      <dd className="isData">
                        <strong>{item.applicants}</strong>
                        <span>応募</span>
                      </dd>
                      <dd className="isData">
                        <strong>
                          {item.applicants > 30
                            ? `${(
                                Math.max(Math.ceil((item.applicants - 30) / 10), 0) *
                                (state.isNonProfitPrice
                                  ? non_profit.per_once_add_option_price
                                  : profit.per_once_add_option_price)
                              ).toLocaleString()}`
                            : "0"}
                        </strong>
                        <span>円</span>
                      </dd>
                    </div>
                  ))}
                </div>
              </div>
            </dl>
          )}
          <p className="calculatedDesc">
            ※クレジットカード支払いの場合、翌月のプランご契約開始日に決済されます。
            <br />
            　例：4/10に契約開始の場合、5/10に決済されます。
            <br />
            <br />
            ※銀行振込の場合のご請求書の送付は、プラン料金は翌月1日、オプション料金は翌々月1日となります。
            <br />
            　例：4/10に契約開始の場合、5/1にプレミアムプランの請求書送付、6/1にオプション料金の請求書が送付されます
            <br />
            <br />
            ご請求のタイミングに関して、詳しくは
            <a href="https://activo.zendesk.com/hc/ja/articles/25339216979085" target="blank">
              こちらのページ
            </a>
            をご覧ください
          </p>
        </div>
      )}
    </div>
  );
}

PlanSimulator.propTypes = {
  image_urls: PropTypes.shape({
    man: PropTypes.string.isRequired,
    woman: PropTypes.string.isRequired,
  }).isRequired,
  is_logged_in_user: PropTypes.bool.isRequired,
  is_non_profit_user: PropTypes.bool.isRequired,
  prices: PropTypes.shape({
    non_profit: PropTypes.shape({
      monthly_price: PropTypes.number.isRequired,
      per_year_price: PropTypes.number.isRequired,
      per_once_add_option_price: PropTypes.number.isRequired,
    }).isRequired,
    profit: PropTypes.shape({
      monthly_price: PropTypes.number.isRequired,
      per_year_price: PropTypes.number.isRequired,
      per_once_add_option_price: PropTypes.number.isRequired,
    }).isRequired,
  }).isRequired,
};

if (document.getElementById('planSimulation') !== null) {
document.addEventListener("DOMContentLoaded", () => {
  const planSimulationElement = document.getElementById("planSimulation");
  if (planSimulationElement) {
    const props = JSON.parse(planSimulationElement.getAttribute("data-props"));
    ReactDOM.render(
      <PlanSimulator
        image_urls={props.image_urls}
        is_logged_in_user={props.is_logged_in_user}
        is_non_profit_user={props.is_non_profit_user}
        prices={props.prices}
      />,
      planSimulationElement
    );
  }
});
}
